import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import { showToast } from "./toast";
import type { GameDecksResponse, GameMode, GameSession } from "./types";
import type {
  GameAnsweredData,
  GameCardDrawnData,
  GameDeclinedData,
  GameEndedData,
  GameFinishedData,
  GameInviteData,
  GameJoinedData,
  GameSkippedData,
} from "./chatSocket";

// Framework-agnostic Game data layer, same shape as togetherApi.ts -
// React-only feature, appState/notifyStateChange used for consistency with
// every other list/session in this app.

export type GameResult<T extends object = { session?: never }> = ({ ok: true } & T) | { ok: false; error?: string };

export async function inviteGameApi(
  params: { recipientEmail?: string; mode: GameMode; intensity?: number | null; rematch?: boolean },
): Promise<GameResult<{ session: GameSession }>> {
  const res = await apiFetch("/api/game/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.gameSession = data.session as GameSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function acceptGameApi(sessionId: string): Promise<GameResult<{ session: GameSession }>> {
  const res = await apiFetch(`/api/game/${sessionId}/accept`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.gameIncomingInvite = null;
  appState.gameSession = data.session as GameSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function declineGameApi(sessionId: string): Promise<GameResult> {
  const res = await apiFetch(`/api/game/${sessionId}/decline`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.gameIncomingInvite = null;
  notifyStateChange();
  return { ok: true };
}

export async function drawCardApi(sessionId: string): Promise<GameResult<{ session: GameSession }>> {
  const res = await apiFetch(`/api/game/${sessionId}/draw`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.gameSession = data.session as GameSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function answerCardApi(sessionId: string, text: string): Promise<GameResult<{ session: GameSession }>> {
  const res = await apiFetch(`/api/game/${sessionId}/answer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.gameSession = data.session as GameSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function skipCardApi(sessionId: string): Promise<GameResult<{ session: GameSession }>> {
  const res = await apiFetch(`/api/game/${sessionId}/skip`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.gameSession = data.session as GameSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function leaveGameApi(sessionId: string): Promise<GameResult> {
  const res = await apiFetch(`/api/game/${sessionId}/leave`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.gameSession = null;
  notifyStateChange();
  return { ok: true };
}

// Read-only lookup of this user's current session state, straight from the
// server (not appState) - used by GameRoot.tsx's reconnect-resync effect,
// which needs ground truth after a socket drop (or a fresh page load while
// a game is already active), same reasoning as togetherApi.ts's
// fetchTogetherSessionApi.
export async function fetchGameSessionApi(): Promise<GameSession | null> {
  const res = await apiFetch("/api/game/session");
  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return (data.session as GameSession | null) ?? null;
}

// Deck metadata rarely/never changes at runtime - fetched once and reused
// (module-level cache, not appState: this is static reference data, not a
// live session) rather than re-fetching every time the lobby opens.
let gameDecksCache: Promise<GameDecksResponse | null> | null = null;

export async function fetchGameDecksApi(): Promise<GameDecksResponse | null> {
  if (!gameDecksCache) {
    gameDecksCache = (async () => {
      try {
        const res = await apiFetch("/api/game/decks");
        if (res.status === 401) {
          requestLock("unauthorized");
          return null;
        }
        if (!res.ok) return null;
        return (await res.json().catch(() => null)) as GameDecksResponse | null;
      } catch {
        // A genuine network failure (offline, DNS hiccup, aborted request)
        // makes apiFetch() throw, not just resolve non-ok - without this
        // catch, gameDecksCache above got permanently set to a REJECTED
        // promise, so the "allow a retry" line below never ran (the
        // function threw before reaching it), and every future call just
        // re-threw that same stale rejection forever. Resolving to null
        // here instead lets the reset below actually happen.
        return null;
      }
    })();
  }
  const result = await gameDecksCache;
  if (!result) gameDecksCache = null; // allow a retry on the next call instead of caching a failure forever
  return result;
}

// --- Apply incoming WS pushes to appState (called from the socket handler
// registry GameRoot.tsx sets up via setGameSocketHandlers) ---

export function applyGameInvite(data: GameInviteData): void {
  // Ignore a stray invite if this client is somehow already in a session
  // (the server itself already rejects double-invites via 409, but a
  // client can still race the push with its own local state).
  if (appState.gameSession) return;
  appState.gameIncomingInvite = data;
  notifyStateChange();
}

export function applyGameJoined(data: GameJoinedData): void {
  if (!appState.gameSession || appState.gameSession.id !== data.sessionId) return;
  appState.gameSession = { ...appState.gameSession, status: "active", isMyTurn: data.isMyTurn };
  notifyStateChange();
}

export function applyGameDeclined(data: GameDeclinedData): void {
  if (!appState.gameSession || appState.gameSession.id !== data.sessionId) return;
  showToast(`${appState.gameSession.peerName} declined your game invite.`, "info");
  appState.gameSession = null;
  notifyStateChange();
}

export function applyGameCardDrawn(data: GameCardDrawnData): void {
  if (!appState.gameSession || appState.gameSession.id !== data.sessionId) return;
  appState.gameSession = {
    ...appState.gameSession,
    // Receiving a card someone else drew always means it's not my turn -
    // only the turn-holder can draw, and this push only ever reaches the
    // other participant.
    isMyTurn: false,
    currentCard: {
      cardId: data.cardId,
      deckId: data.deckId,
      category: data.category,
      text: data.text,
      chatMessageId: data.chatMessageId,
      resolution: "drawn",
    },
    cardsPlayed: data.progress - 1,
  };
  notifyStateChange();
}

export function applyGameAnswered(data: GameAnsweredData): void {
  if (!appState.gameSession || appState.gameSession.id !== data.sessionId) return;
  const currentCard = appState.gameSession.currentCard;
  appState.gameSession = {
    ...appState.gameSession,
    isMyTurn: data.isMyTurn,
    currentCard: currentCard ? { ...currentCard, resolution: "answered" } : null,
    cardsPlayed: appState.gameSession.cardsPlayed + 1,
  };
  notifyStateChange();
}

export function applyGameSkipped(data: GameSkippedData): void {
  if (!appState.gameSession || appState.gameSession.id !== data.sessionId) return;
  const currentCard = appState.gameSession.currentCard;
  appState.gameSession = {
    ...appState.gameSession,
    isMyTurn: data.isMyTurn,
    currentCard: currentCard ? { ...currentCard, resolution: "skipped" } : null,
    cardsPlayed: appState.gameSession.cardsPlayed + 1,
    cardsSkipped: appState.gameSession.cardsSkipped + 1,
  };
  notifyStateChange();
}

export function applyGameFinished(data: GameFinishedData): void {
  if (!appState.gameSession || appState.gameSession.id !== data.sessionId) return;
  appState.gameSession = { ...appState.gameSession, status: "finished" };
  notifyStateChange();
}

export function applyGameEnded(data: GameEndedData): void {
  if (!appState.gameSession || appState.gameSession.id !== data.sessionId) return;
  if (data.reason === "left") showToast(`${appState.gameSession.peerName} ended the game.`, "info");
  else if (data.reason === "disconnected") showToast(`${appState.gameSession.peerName} disconnected.`, "info");
  else if (data.reason === "expired") showToast("Your game invite expired.", "info");
  appState.gameSession = null;
  notifyStateChange();
}
